import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import * as yup from 'yup';
import { getItem } from '../actions/pdp.actions';
import { addReview } from '../actions/reviews.actions';
import { REVIEWS_ADD_ITEM_RESET } from '../constants/reviews.constants';
import { colorAttributeCompare, defaultAttributeCompare } from "../features/pdp/attribute-sorting";
import { errorFormatRendered } from '../utils/errorHandler.util';
import Alert from './Alert';
import ColorSwatches from './ColorSwatches';
import Swatches from './Swatches';
import LoadingSpinner from './tailwind/LoadingSpinner';
import WebImage from './tailwind/WebImage';
import { Link } from '@components/Link';

const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ProductAddReviewModal = ({ toggleHandler, variants }) => {
  const dispatch = useAppDispatch();

  const { details } = useSelector((state) => state.pdpItem);
  const { loading, success, error } = useSelector((state) => state.reviewsAdd);

  const [images, setImages] = useState([]);
  const [imagesError, setImagesError] = useState(null);

  const { register, handleSubmit, formState } = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      yup.object().shape({
        rating: yup
          .number()
          .min(1, 'Minimum rating is 1 star')
          .max(5, 'Maximum rating is 5 stars')
          .required('Please select a rating'),
        title: yup.string().required('Please enter a title for your review'),
        body: yup.string().required('Please enter a description for your review'),
      })
    ),
  });

  const { errors, isValid } = formState;

  const modal = useRef(null);

  useEffect(() => () => dispatch({ type: REVIEWS_ADD_ITEM_RESET }), [dispatch]);

  useEffect(() => {
    if (success) {
      toggleHandler(false);
    }
  }, [success]);

  const closeModal = () => {
    modal.current.classList.add('add-review-modal--hiding');

    setTimeout(() => {
      if (modal.current) {
        modal.current.classList.remove('add-review-modal--hiding');
      }
      toggleHandler();
    }, 1000);
  };

  const { itemId } = details;

  const onSubmit = (data) => {
    dispatch(addReview({ ...data, images, itemId }));
  };

  const removeImageHandler = (index) => () => {
    images.splice(index, 1);
    setImages([...images]);
  };

  const addImageHandler = (e) => {
    const file = e.target.files[0];

    if (file && !SUPPORTED_FORMATS.includes(file.type)) {
      setImagesError('Unsupported format');
    } else if (file && file.size >= MAX_FILE_SIZE) {
      setImagesError('Image is too large');
    } else if (images.length >= 10) {
      setImagesError("You can't upload more than 10 images");
    } else {
      setImagesError(null);
      setImages([...images, file]);
    }
  };

  return (
    <div ref={modal} className='add-review-modal' onClick={closeModal}>
      <div className='add-review-modal__dialog' onClick={(e) => e.stopPropagation()}>
        <span className='add-review-modal__close' onClick={closeModal}>
          &times;
        </span>
        &nbsp;
        <div className='add-review-modal__body'>
          {loading && <LoadingSpinner small />}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='container'>
              <SwatchesDropdown {...{ details, variants }} />
              <div className='row'>
                <div className='col-12'>
                  <div className='form-group'>
                    <label htmlFor='rating'>Your Rating</label>
                    <select className='form-control' {...register('rating')} id='rating'>
                      <option value='5'>5 stars</option>
                      <option value='4'>4 stars</option>
                      <option value='3'>3 stars</option>
                      <option value='2'>2 stars</option>
                      <option value='1'>1 star</option>
                    </select>
                    <Alert type='danger' shown={!!errors.rating?.message} asText>
                      {errors.rating?.message}
                    </Alert>
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-12'>
                  <div className='form-group'>
                    <label htmlFor='review-title'>Review Title</label>
                    <input
                      type='text'
                      {...register('title')}
                      id='review-title'
                      placeholder='(required)'
                      className='form-control'
                    />
                    <Alert type='danger' shown={!!errors.title?.message} asText>
                      {errors.title?.message}
                    </Alert>
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-12'>
                  <div className='form-group'>
                    <label htmlFor='review-description'>Review Description</label>
                    <textarea
                      {...register('body')}
                      id='review-description'
                      rows='3'
                      placeholder='(required)'
                      className='form-control'></textarea>
                    <Alert type='danger' shown={!!errors.body?.message} asText>
                      {errors.body?.message}
                    </Alert>
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-12'>
                  <div className='form-group'>
                    <label htmlFor='review-images'>Add Photos to Your Review</label>
                    <input
                      type='file'
                      name='review-images'
                      id='review-images'
                      onChange={addImageHandler}
                      className='d-none'
                    />
                    <ul className='add-review-images'>
                      {images.map((image, index) => (
                        <li key={index}>
                          <img src={URL.createObjectURL(image)} alt={details?.displayName} />
                          <span onClick={removeImageHandler(index)} className='add-review-images__remove'>
                            <i className='fa fa-close'></i>
                          </span>
                        </li>
                      ))}
                      <li>
                        <label htmlFor='review-images'>
                          <span>+</span>
                        </label>
                      </li>
                    </ul>
                    <Alert type='danger' shown={!!imagesError} asText>
                      {imagesError}
                    </Alert>
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-12 text-center my-2'>
                  <button disabled={!isValid} className='twc-btn twc-btn--primary'>
                    Submit
                  </button>
                  {error && (
                    <Alert type='danger' shown>
                      {errorFormatRendered(error)}
                    </Alert>
                  )}
                  <p className='add-review-modal__note'>
                    By submitting this review you agree on our{' '}
                    <Link target='_blank' href={jsRoutes.controllers.Application.static('terms').url}>
                      Terms of Use
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SwatchesDropdown = ({ details, variants }) => {
  const [hidden, setHidden] = useState(!details.isGenericProduct);
  const dispatch = useAppDispatch();
  const root = useRef();

  const changeItemHandler = useCallback(
    (swatchData) => async () => {
      dispatch(getItem(swatchData.url));
    },
    [dispatch]
  );
  const toggleHidden = () => setHidden(!hidden);

  useEffect(() => {
    if (details.isGenericProduct) {
      setTimeout(() => setHidden(false), 0);
    }
  }, [details.isGenericProduct]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!details.isGenericProduct && !root.current?.contains?.(e.target)) {
        setHidden(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [details.isGenericProduct, root.current]);

  return (
    <div ref={root} className='pdp-swatches'>
      <div className='form-group'>
        <div className='card'>
          <div className='card-body'>
            <div
              className='d-flex flex-row justify-content-between align-items-center'
              style={{ cursor: 'pointer' }}
              data-bs-toggle='collapse'
              data-bs-target='#collapseExample'
              aria-expanded='true'
              aria-controls='collapseExample'
              onClick={toggleHidden}>
              {details.isGenericProduct && (
                <h6 className='fw-bold text-start text-muted'>What product are you reviewing?</h6>
              )}
              {!details.isGenericProduct && (
                <>
                  <WebImage
                    webImage={details.webImages.default.thumb}
                    alt={details.displayName}
                    className='img-fluid'
                  />
                  <h6 className='fw-bold text-start'>{details?.displayName}</h6>
                  <i className={`fa fa-chevron-${hidden ? 'down' : 'up'}`}></i>
                </>
              )}
            </div>
            <div className={'collapse' + (!hidden ? ' show' : '')} id='collapseExample'>
              {variants?.show.genders && (
                <Swatches
                  label='Gender'
                  attributes={variants?.genders?.toSorted(defaultAttributeCompare)}
                  activeAttribute={variants?.activeSex}
                  itemHandler={changeItemHandler}
                  wrapperClassName='pdp-swatches__item pdp-swatches__item-gender'
                />
              )}
              {variants?.show.names && (
                <Swatches
                  label='Name'
                  attributes={variants?.names?.toSorted(defaultAttributeCompare)}
                  activeAttribute={variants?.activeName}
                  itemHandler={changeItemHandler}
                  wrapperClassName='pdp-swatches__item pdp-swatches__item-name'
                />
              )}
              {variants?.show.sizes && (
                <Swatches
                  label='Size'
                  hideLabel={details.isGenericProduct}
                  showActiveAttribute={false}
                  attributes={variants?.sizes?.toSorted(defaultAttributeCompare)}
                  activeAttribute={variants?.activeSize}
                  itemHandler={changeItemHandler}
                  wrapperClassName='pdp-swatches__item pdp-swatches__item-size'
                />
              )}
              {variants?.show.colors && (
                <ColorSwatches
                  selectedInTitle={false}
                  showBaseTitle={!details.isGenericProduct}
                  colors={variants?.colors?.toSorted(colorAttributeCompare)}
                  activeColor={variants?.activeColor}
                  itemHandler={changeItemHandler}
                  wrapperClassName='pdp-swatches__item pdp-swatches__item-color'
                />
              )}
              {variants?.show.materials && (
                <Swatches
                  label='Material'
                  showActiveAttribute={false}
                  attributes={variants?.materials?.toSorted(defaultAttributeCompare)}
                  activeAttribute={variants?.activeMaterial}
                  itemHandler={changeItemHandler}
                  wrapperClassName='pdp-swatches__item pdp-swatches__item-material'
                />
              )}
              {variants?.show.widths && (
                <Swatches
                  label='Width'
                  showActiveAttribute={false}
                  attributes={variants?.widths?.toSorted(defaultAttributeCompare)}
                  activeAttribute={variants?.activeWidth}
                  itemHandler={changeItemHandler}
                  wrapperClassName='pdp-swatches__item pdp-swatches__item-material'
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductAddReviewModal;
